import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ClaimFile } from '../../../api/files/interface';
import { actions as filesSliceActions } from '../../../data/files/logic';
import type { StoreState } from '../../../data/store';

export interface FilesHookState {
  files: Record<string, ClaimFile>;
  paginatedFileIds: string[];
  fileFilters: { tags: string[]; scopes: string[] };
  filesCount: number;
  unsupportedFilesCount: number;
  unsupportedFileTypes: string[];
  filesContent?: {
    [fileId: string]: {
      [pageNumber: string]: {
        url: string;
        requestedDate: number;
      };
    };
  };
  filesOCR?: {
    [fileId: string]: {
      [dataVersion: string]: {
        [pageNumber: string]: string;
      };
    };
  };
  actions: StoreState['files']['actions'];
}

const EMPTY_FILES: Record<string, ClaimFile> = {};
const EMPTY_FILE_IDS: string[] = [];
const EMPTY_FILE_FILTERS = { tags: [], scopes: [] };

interface FilesHookHandler {
  fetchFiles(data?: {
    stateDb?: boolean;
    limit?: number;
    offset?: number;
    tags?: string[];
    scopes?: string[];
  }): void;
  fetchFile(data: { fileId: string; stateDb?: boolean }): void;
  fetchTags(): void;
  fetchFileContents(data: {
    fileId: string;
    startPage: number;
    endPage: number;
  }): void;
  downloadFile(fileId: string): void;
  deleteFiles(fileIds: string[]): void;
  updateFileMetadata(
    files: Array<{
      file_id: string;
      tags?: string[];
      fileScope?: string | null;
      title?: string | null;
    }>
  ): void;
  fetchFilesOCR(data: { fileId: string; dataVersion: string }): void;
  fetchFileSource(data: { fileId: string }): void;
}

const useFilesHook = (
  dossierId: string
): [FilesHookState, FilesHookHandler] => {
  const dispatch = useDispatch();
  const {
    files,
    paginatedFileIds,
    fileFilters,
    filesCount,
    unsupportedFilesCount,
    unsupportedFileTypes,
    filesContent,
    filesOCR,
    actions,
  } = useSelector((s: StoreState) => s['files']);

  const handler = React.useMemo(
    () => ({
      fetchFiles: (data?: {
        stateDb?: boolean;
        limit?: number;
        offset?: number;
        tags?: string[];
        scopes?: string[];
      }) => {
        dispatch(
          filesSliceActions.files({
            dossier_id: dossierId,
            stateDb: data?.stateDb,
            limit: data?.limit,
            offset: data?.offset,
            tags: data?.tags,
            scopes: data?.scopes,
          })
        );
      },
      fetchFile: (data: { fileId: string; stateDb?: boolean }) => {
        dispatch(
          filesSliceActions.fetchFile({
            dossier_id: dossierId,
            file_id: data.fileId,
            stateDb: data.stateDb,
          })
        );
      },
      fetchTags: () => {
        dispatch(filesSliceActions.fetchFileFilters({ dossier_id: dossierId }));
      },
      fetchFileContents: (data: {
        fileId: string;
        startPage: number;
        endPage: number;
      }) => {
        const { fileId, startPage, endPage } = data;
        dispatch(
          filesSliceActions.fetchFileContents({
            dossierId,
            fileId,
            startPage,
            endPage,
          })
        );
      },
      downloadFile: (fileId: string) => {
        dispatch(filesSliceActions.downloadFile(fileId));
      },
      deleteFiles: (fileIds: string[]) => {
        dispatch(filesSliceActions.deleteFiles({ dossierId, fileIds }));
      },
      updateFileMetadata: (
        filesToUpdate: Array<{
          file_id: string;
          tags?: string[];
          fileScope?: string | null;
          title?: string | null;
        }>
      ) => {
        dispatch(
          filesSliceActions.updateFileMetadata({
            dossierId,
            files: filesToUpdate,
          })
        );
      },
      fetchFilesOCR: (data: { fileId: string; dataVersion: string }) => {
        const { fileId, dataVersion } = data;
        dispatch(
          filesSliceActions.fetchFilesOCR({
            dossierId,
            fileId,
            dataVersion,
          })
        );
      },
      fetchFileSource: (data: { fileId: string }) => {
        const { fileId } = data;
        dispatch(filesSliceActions.fetchFileSource({ dossierId, fileId }));
      },
    }),
    [dossierId]
  );

  return [
    {
      files: files[dossierId] || EMPTY_FILES,
      paginatedFileIds: paginatedFileIds[dossierId] || EMPTY_FILE_IDS,
      fileFilters: fileFilters[dossierId] || EMPTY_FILE_FILTERS,
      filesCount: filesCount[dossierId] || 0,
      unsupportedFilesCount: unsupportedFilesCount[dossierId] || 0,
      unsupportedFileTypes: unsupportedFileTypes[dossierId] || [],
      filesContent,
      filesOCR,
      actions,
    },
    handler,
  ];
};

export default useFilesHook;
